import { AvailableFilterFields, FilterFieldType } from '@core/datafilter/available-filter-fields';
import { Filter } from '@core/datafilter/filter';
import { tryParseDate } from '@shared/functions/try-parse-date';
import { BehaviorSubject, Observable } from 'rxjs';
import { FilterCondition } from '@core/datafilter/filter-condition';
import { debounceTime, distinctUntilChanged, filter } from 'rxjs/operators';
import { HttpParams } from '@angular/common/http';
import { FormArray, FormControl, FormGroup } from '@angular/forms';
import { U2bColumnDefinition, U2bColumnDefinitionFilterSettings } from '@core/components/layout/table/table.types';
import { DEFAULT_DEBOUNCE_TIME } from '@modules/bcm/@shared/constants';
import { FilterRaw } from '@core/datafilter/filter-raw';
import { toSqlDate } from '@core/functions/to-date-string';
import { isPlainObject } from '@shared/functions/is-plain-object';
import { DataFilterOperator } from '@core/datafilter/filter-operator';
import { DataFilterOperationType } from '@core/datafilter/filter-operation-type';
import { buildTenantScopedSessionKey } from '@modules/bcm/@shared/utils/tenant-scoped-storage.util';

export class DataFilterService<Entity> {

    private _availableFilterFields: AvailableFilterFields;

    private get availableFilterFields(): AvailableFilterFields {
        return this._availableFilterFields;
    }

    private _currentFilter$ = new BehaviorSubject<Filter<Entity>>(new Filter());

    public get currentFilter$(): BehaviorSubject<Filter<Entity>> {
        return this._currentFilter$;
    }

    private _currentFilterParams$ = new BehaviorSubject<HttpParams>(new HttpParams());

    public get currentFilterParams$(): Observable<HttpParams> {
        return this._currentFilterParams$;
    }

    private _additionalFilterParams$ = new BehaviorSubject<HttpParams>(new HttpParams());

    public get additionalFilterParams$(): Observable<HttpParams> {
        return this._additionalFilterParams$;
    }

    /**
     * Facade-owned params (e.g. a list preset) that travel with every server-side filter emission,
     * so the table resets the page and cancels stale requests when they change.
     * With `emitNow = false` they are only picked up by the next filter-form emission.
     */
    public setAdditionalFilterParams(params: HttpParams, emitNow = true): void {
        this._additionalFilterParams$.next(params);

        if (emitNow && this.useBackEndFiltering) {
            this.prepareBackEndFilters(this._allFiltersFormGroup.value);
        }
    }

    // todo:
    //  To save the filter, just save the current form state to db together with a name
    //  Maybe add a own table bcm_table_filters to easier share filters between users?
    protected _allFiltersFormGroup = new FormGroup({});

    public get allFiltersFormGroup(): FormGroup {
        return this._allFiltersFormGroup;
    }

    public get hasActiveFilters(): boolean {
        return Object
            .values(this._allFiltersFormGroup.value)
            .map((formArray: Partial<FilterRaw>[]) => {
                return (formArray || []).find(item => this.isValidActiveFilter(item));
            })
            .flat()
            .filter(item => item != null).length > 0;
    }

    private _forceNextFormValueChange = false;

    /**
     * When set, the next debounced form-value emit is not written to sessionStorage.
     * A synchronous on/off toggle can't be used here: the persistence write lives behind
     * `debounceTime` and runs ~{@link DEFAULT_DEBOUNCE_TIME}ms later, by which point a
     * synchronously-restored flag would already be back on (and the active tenant may have
     * changed). The flag is therefore cleared by the debounced consumer instead.
     */
    private _skipNextPersist = false;

    private readonly _tableId: string;

    private readonly _resolveTenantId: () => number | null | undefined;

    constructor(tableId: string,
                columnDefinitions: U2bColumnDefinition[],
                private useBackEndFiltering = false,
                resolveTenantId: () => number | null | undefined = () => undefined) {

        this._tableId = tableId;
        this._resolveTenantId = resolveTenantId;

        this.setupFilterChangeSubscription();
        this.setupAvailableFilters(columnDefinitions);
    }

    private get _filtersStorageKey(): string {
        return buildTenantScopedSessionKey(this._resolveTenantId(), `${this._tableId}_filters`);
    }

    public isValidActiveFilter(item: Partial<FilterRaw>): boolean {
        if (item.operator === DataFilterOperator.IsEmpty) {
            return true;
        }

        if (Array.isArray(item.value)) {
            return item.value.length > 0;
        }

        return item.value != null && item.value !== '';
    }

    public resetActiveFilters(skipSessionPersist = false) {
        if (skipSessionPersist) {
            this._skipNextPersist = true;
        }

        this._allFiltersFormGroup.reset();
        Object
            .values(this._allFiltersFormGroup.controls)
            .forEach((myFormControl: FormArray) => myFormControl.clear());
        this._forceNextFormValueChange = true;
        this._allFiltersFormGroup.updateValueAndValidity();
    }

    public forceNextFormValueChange(): void {
        this._forceNextFormValueChange = true;
    }

    /** Re-applies persisted filters for the current tenant scope (after harbour switch). */
    public reloadPersistedFilters(): void {
        this.resetActiveFilters(true);
        this._loadPersistedFilters();
    }

    private _loadPersistedFilters(): void {
        const persistedFilters = JSON.parse(sessionStorage.getItem(this._filtersStorageKey) || '{}');

        Object.keys(persistedFilters).forEach(key => {
            const value = persistedFilters[key];
            const formArray = this._allFiltersFormGroup.get(key);

            if (formArray instanceof FormArray && Array.isArray(value)) {
                for (const item of value) {
                    this.addFilterToFormArray(formArray, item);
                }
            }
        });

        this._allFiltersFormGroup.patchValue(persistedFilters);
    }

    private setupAvailableFilters(columnDefinitions: U2bColumnDefinition[]): void {
        this._availableFilterFields = (columnDefinitions || [])
            .filter(columnDefinition => columnDefinition.filter != null)
            .map(columnDefinition => {
                const columnDefinitionFilter = columnDefinition.filter as U2bColumnDefinitionFilterSettings;

                this.addFormArray(columnDefinition);

                return {
                    field: columnDefinitionFilter.property,
                    fieldType: columnDefinitionFilter.fieldType,
                    operators: columnDefinitionFilter.operators,
                };
            });

        this._loadPersistedFilters();
    }

    private getFieldType(field: string): FilterFieldType {
        return this.availableFilterFields.find((x) => x.field === field)?.fieldType || FilterFieldType.Text;
    }

    private parseValueByFieldName<T = unknown>(value: T, field: string): Date | string | boolean | T {

        const fieldType = this.getFieldType(field);

        switch (fieldType) {
            case FilterFieldType.Date:
                return tryParseDate(value);
            case FilterFieldType.Text:
                return value;
            case FilterFieldType.Boolean:
                return value === 'all' ? 'all' : !!value;
            default:
                return value;
        }
    }

    private formatValueByFieldName<T = unknown>(value: Date | string | T,
                                                field: string,
                                                columnDefinition: U2bColumnDefinition): string | number | boolean {

        const fieldType = this.getFieldType(field);

        switch (fieldType) {
            case FilterFieldType.Date:
                return toSqlDate(value as Date);
            case FilterFieldType.Text:
                return String(value);
            case FilterFieldType.AutoComplete:
                return this.formatValueForSelect(value, columnDefinition);
            case FilterFieldType.Select:
                return this.formatValueForSelect(value, columnDefinition);
            case FilterFieldType.Boolean:
                return value === 'all' ? 'all' : !!value;
            default:
                return String(value);
        }
    }

    private formatValueForSelect<T = unknown>(value: Date | string | T,
                                              columnDefinition: U2bColumnDefinition): string | number | boolean {
        const compareAttribute = Array.isArray(columnDefinition.filter.compareAttribute)
            ? columnDefinition.filter.compareAttribute[0]
            : columnDefinition.filter.compareAttribute;
        return isPlainObject(value)
            ? (value[compareAttribute] || value.valueOf())
            : String(value);
    }

    public getFormArray(columnDefinition: U2bColumnDefinition): undefined | FormArray {
        return this._allFiltersFormGroup.get(columnDefinition.property) as FormArray || undefined;
    }

    public addNewFilterToFormArray(formArray: FormArray, columnDefinition: U2bColumnDefinition) {
        const defaultValue = columnDefinition.filter?.filterMultiple ? [] : null;

        formArray.push(new FormGroup({
            operator: new FormControl(columnDefinition.filter.operators[0]),
            // todo: check why validation of date not working as expected.
            value: new FormControl(defaultValue/*, this.getValidatorsByFieldType()*/),
            operationType: new FormControl(
                formArray.length === 0
                    ? null
                    : DataFilterOperationType.And
            ),
            // save reference of column definition for later usage
            columnDefinition: new FormControl(columnDefinition)
        }));
    }

    public resetFilterFormArray(columnDefinition: U2bColumnDefinition): FormArray {
        const defaultValue = columnDefinition.filter?.filterMultiple ? [] : null;

        const formArray = new FormArray([
            new FormGroup({
                operator: new FormControl(columnDefinition.filter.operators[0]),
                // todo: check why validation of date not working as expected.
                value: new FormControl(defaultValue/*, this.getValidatorsByFieldType()*/),
                operationType: new FormControl(null),
                // save reference of column definition for later usage
                columnDefinition: new FormControl(columnDefinition)
            })
        ]);

        this.forceNextFormValueChange();

        this._allFiltersFormGroup.setControl(columnDefinition.property, formArray);

        return formArray;
    }

    private addFilterToFormArray(formArray: FormArray, data: any) {
        formArray.push(new FormGroup({
            operator: new FormControl(data.operator),
            value: new FormControl(data.value),
            operationType: new FormControl(data.operationType),
            // save reference of column definition for later usage
            columnDefinition: new FormControl(data.columnDefinition)
        }));
    }

    private addFormArray(columnDefinition: U2bColumnDefinition) {
        this._allFiltersFormGroup.addControl(columnDefinition.property, new FormArray([]));
    }

    private setupFilterChangeSubscription() {

        let hadActiveFilters = false;

        this._allFiltersFormGroup
            .valueChanges
            .pipe(
                debounceTime(DEFAULT_DEBOUNCE_TIME),
                filter((formValue) => {
                    if (this._forceNextFormValueChange) {
                        this._forceNextFormValueChange = false;
                        return true;
                    }

                    // check active filters directly from the emitted formValue (not via hasActiveFilters getter
                    // which reads the live FormGroup state and may differ from the debounced emit)
                    const hasActiveNow = Object
                        .values(formValue as Record<string, Partial<FilterRaw>[]>)
                        .some(arr => (arr || []).some(item => this.isValidActiveFilter(item)));

                    const pass = hasActiveNow || hadActiveFilters;
                    hadActiveFilters = hasActiveNow;
                    return pass;
                })
            )
            .subscribe(formValue => {
                if (this.useBackEndFiltering) {
                    this.prepareBackEndFilters(formValue);
                } else {
                    this.prepareFrontEndFilters(formValue);
                }

                // Clearing the flag here (rather than synchronously in resetActiveFilters) is what
                // makes the suppression survive the debounce window: the empty emit produced by a
                // reset is consumed and skipped, while later genuine user changes persist as normal.
                if (this._skipNextPersist) {
                    this._skipNextPersist = false;
                    return;
                }

                sessionStorage.setItem(this._filtersStorageKey, JSON.stringify(formValue));
            });
    }

    /**
     * Builds HttpParams for backend table filtering/export from the active filter form state.
     * Used for server-side tables and for client-side tables whose export runs on the backend.
     */
    public buildBackendFilterParams(formValue = this._allFiltersFormGroup.value): HttpParams {
        let httpParams = new HttpParams();

        // unreserved characters: -._~
        // produce a string for each property like:
        //      "property=endsWith~abc.and.includes~test.or.includes~test2"
        // while back end parses this to sql like this:
        //      "? like '%abc' and (? like '%test%' or ? like '%test2%')"
        Object
            .entries(formValue)
            .forEach(([property, formArray]: [string, Partial<FilterRaw>[]]) => {

                // The form-control key is the column's `property`, which is what the user sees in
                // the column header (e.g. 'm²', 'statusIndex', 'pierHandle'). The backend, however,
                // expects the *filter* property (e.g. 'squareMeters', 'status', 'pier') because
                // that's the canonical attribute name on the resource. Prefer the filter override
                // when one is configured; otherwise fall back to the column property.
                const urlKey = formArray?.[0]?.columnDefinition?.filter?.property ?? property;

                const propertyValue = formArray
                    .filter(filterFormGroup => this.isValidActiveFilter(filterFormGroup))
                    .map((filterFormGroup, index, array) => {

                        const operationType = index < array.length - 1
                            ? `.${filterFormGroup.operationType}.`
                            : '';

                        if (filterFormGroup.operator === DataFilterOperator.IsEmpty) {
                            return `${filterFormGroup.operator}~${operationType}`;
                        }

                        if (filterFormGroup.columnDefinition.filter?.filterMultiple
                            && Array.isArray(filterFormGroup.value)) {
                            const joinOperationType = filterFormGroup.operator === DataFilterOperator.NotContains
                                ? DataFilterOperationType.And
                                : DataFilterOperationType.Or;

                            const serialized = filterFormGroup.value
                                .map((entry, entryIndex, entries) => {
                                    const formatted = this.formatValueByFieldName(
                                        entry,
                                        filterFormGroup.columnDefinition.filter?.property
                                            ?? filterFormGroup.columnDefinition.property,
                                        filterFormGroup.columnDefinition,
                                    );
                                    const innerOperationType = entryIndex < entries.length - 1
                                        ? `.${joinOperationType}.`
                                        : '';
                                    return `${filterFormGroup.operator}~${formatted}${innerOperationType}`;
                                })
                                .join('');

                            return `${serialized}${operationType}`;
                        }

                        const parsedValue = this.formatValueByFieldName(
                            filterFormGroup.value,
                            filterFormGroup.columnDefinition.filter?.property
                                ?? filterFormGroup.columnDefinition.property,
                            filterFormGroup.columnDefinition,
                        );

                        return `${filterFormGroup.operator}~${parsedValue}${operationType}`;
                    })
                    .join('');

                if (propertyValue) {
                    httpParams = httpParams.set(urlKey, propertyValue);
                }
            });

        return httpParams;
    }

    private prepareBackEndFilters(formValue: any) {
        const additionalParams = this._additionalFilterParams$.getValue();
        let httpParams = this.buildBackendFilterParams(formValue);

        additionalParams.keys().forEach(key => {
            httpParams = httpParams.set(key, additionalParams.get(key));
        });

        this._currentFilterParams$.next(httpParams);
    }

    private prepareFrontEndFilters(formValue: any) {
        const currentFilter = new Filter<Entity>();

        Object
            .entries(formValue)
            .forEach(([, formArray]: [string, Partial<FilterRaw>[]]) => {
                formArray
                    .filter(filterFormGroup =>
                        filterFormGroup.operator === DataFilterOperator.IsEmpty
                        || ![undefined, null, ''].includes(filterFormGroup.value)
                    )
                    .forEach(filterFormGroup => {
                        let parsedValue: string | number | boolean;

                        if (filterFormGroup.operator === DataFilterOperator.IsEmpty) {
                            parsedValue = '';
                        } else {
                            parsedValue = this.parseValueByFieldName(
                                filterFormGroup.value,
                                filterFormGroup.columnDefinition.filter.property
                            );
                        }

                        currentFilter.addCondition(
                            new FilterCondition(
                                filterFormGroup.columnDefinition.filter.property,
                                filterFormGroup.operator,
                                parsedValue,
                                filterFormGroup.columnDefinition.filter.fieldType,
                                filterFormGroup.columnDefinition.filter.compareAttribute,
                                filterFormGroup.operationType ?? DataFilterOperationType.And,
                            ),
                            true
                        );
                    });
            });

        this._currentFilter$.next(currentFilter);
    }
}
